import {Component, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {HttpErrorResponse} from "@angular/common/http";
import {debounceTime, distinctUntilChanged, interval, Subject, Subscription} from "rxjs";
import {TableModule} from "primeng/table";
import {SelectModule} from "primeng/select";
import {InputTextModule} from "primeng/inputtext";
import {ToggleSwitchModule} from "primeng/toggleswitch";
import {ButtonModule} from "primeng/button";
import {TooltipModule} from "primeng/tooltip";
import {ProgressBarModule} from "primeng/progressbar";
import {LogService} from "../../core/services/log.service";
import {ToastService} from "../../core/services/toast.service";
import {ToolbarComponent} from "../../shared/components/toolbar/toolbar.component";
import {ALL_LOG_SERVICES, ILogEntry, ILogPage, ILogSource, LogLevel} from "../../core/models/log.model";

interface LogRow extends ILogEntry {
  key: number;
}

const AUTO_REFRESH_MS = 10_000;

@Component({
    selector: 'app-log',
    imports: [
        CommonModule,
        FormsModule,
        TableModule,
        SelectModule,
        InputTextModule,
        ToggleSwitchModule,
        ButtonModule,
        TooltipModule,
        ProgressBarModule,
        ToolbarComponent
    ],
    templateUrl: './log.component.html',
    styleUrl: './log.component.scss'
})
export class LogComponent implements OnInit, OnDestroy {

  readonly ALL = ALL_LOG_SERVICES;
  readonly levelOptions: LogLevel[] = ['ERROR', 'WARN', 'INFO', 'DEBUG'];
  readonly limitOptions = [200, 500, 1000, 2000].map(v => ({label: `${v} lignes`, value: v}));

  sources: ILogSource[] = [];
  service = ALL_LOG_SERVICES;
  date = this.today();
  dateOptions: { label: string, value: string }[] = [];
  levels: LogLevel[] = [];
  query = '';
  user = '';
  limit = 500;
  autoRefresh = false;

  rows: LogRow[] = [];
  page: ILogPage | null = null;
  loading = false;
  errorMessage: string | null = null;
  expandedRows: { [key: number]: boolean } = {};

  private readonly textChange = new Subject<string>();
  private readonly subscriptions = new Subscription();
  private refreshSubscription?: Subscription;

  constructor(private logService: LogService, private toastService: ToastService) { }

  ngOnInit() {
    this.subscriptions.add(
      this.textChange.pipe(debounceTime(400), distinctUntilChanged()).subscribe(() => this.load())
    );
    this.loadSources();
    this.load();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.refreshSubscription?.unsubscribe();
  }

  get isToday(): boolean {
    return this.date === this.today();
  }

  get errorCount(): number {
    return this.rows.filter(r => r.level === 'ERROR').length;
  }

  get warnCount(): number {
    return this.rows.filter(r => r.level === 'WARN').length;
  }

  sourceLabel(service: string): string {
    return this.sources.find(s => s.service === service)?.label ?? service;
  }

  shortLogger(logger: string): string {
    return logger.substring(logger.lastIndexOf('.') + 1);
  }

  selectService(service: string) {
    if (this.service === service) return;
    this.service = service;
    this.buildDateOptions();
    this.load();
  }

  onDateChange() {
    if (!this.isToday) this.setAutoRefresh(false);
    this.load();
  }

  toggleLevel(level: LogLevel) {
    this.levels = this.levels.includes(level) ? this.levels.filter(l => l !== level) : [...this.levels, level];
    this.load();
  }

  /** Clic sur un compteur : n'afficher que ce niveau (ou tout réafficher s'il est déjà seul). */
  focusLevel(level: LogLevel) {
    this.levels = this.levels.length === 1 && this.levels[0] === level ? [] : [level];
    this.load();
  }

  onTextChange() {
    this.textChange.next(`${this.query}\u0000${this.user}`);
  }

  setAutoRefresh(enabled: boolean) {
    this.autoRefresh = enabled;
    this.refreshSubscription?.unsubscribe();
    this.refreshSubscription = undefined;
    if (enabled) {
      this.refreshSubscription = interval(AUTO_REFRESH_MS).subscribe(() => {
        if (!this.loading) this.load(true);
      });
    }
  }

  refresh() {
    this.loadSources();
    this.load();
  }

  load(silent = false) {
    if (!silent) this.loading = true;
    this.logService.getLogs({
      service: this.service,
      date: this.date,
      levels: this.levels,
      q: this.query.trim() || undefined,
      user: this.user.trim() || undefined,
      limit: this.limit
    }).subscribe({
      next: page => {
        this.page = page;
        this.rows = page.entries.map((entry, index) => ({...entry, key: index}));
        this.expandedRows = {};
        this.errorMessage = null;
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.page = null;
        this.rows = [];
        this.errorMessage = typeof err.error === 'string' && err.error
          ? err.error
          : 'Impossible de récupérer les logs. Merci de réessayer plus tard.';
        this.loading = false;
      }
    });
  }

  download() {
    if (this.service === ALL_LOG_SERVICES) return;
    this.logService.download(this.service, this.date).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.service}-${this.date}.log`;
        link.click();
        URL.revokeObjectURL(url);
      },
      error: () => this.toastService.showToast('Logs', 'Le fichier de logs est introuvable pour ce jour.', 'error')
    });
  }

  copy(row: LogRow) {
    const text = [
      `${row.timestamp} ${row.level} [${row.service}] ${row.logger}`,
      row.message,
      row.stackTrace ?? ''
    ].join('\n').trim();
    navigator.clipboard.writeText(text)
      .then(() => this.toastService.showToast('Logs', 'Entrée copiée dans le presse-papiers.', 'success'));
  }

  private loadSources() {
    this.logService.getSources().subscribe({
      next: sources => {
        this.sources = sources;
        this.buildDateOptions();
      },
      error: () => this.toastService.showToast('Logs', "Impossible de récupérer l'état des services.", 'error')
    });
  }

  /** Jours proposés : ceux du service choisi, ou l'union de tous les services pour « Tous ». */
  private buildDateOptions() {
    const selected = this.service === ALL_LOG_SERVICES ? this.sources : this.sources.filter(s => s.service === this.service);
    const dates = [...new Set([this.today(), ...selected.flatMap(s => s.dates)])].sort().reverse();
    this.dateOptions = dates.map(d => ({label: d === this.today() ? `Aujourd'hui (${this.formatDay(d)})` : this.formatDay(d), value: d}));
    if (!dates.includes(this.date)) this.date = this.today();
  }

  private today(): string {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }

  private formatDay(isoDay: string): string {
    const [y, m, d] = isoDay.split('-');
    return `${d}/${m}/${y}`;
  }
}
